import React, { useCallback, useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  discoveryApiRef,
  fetchApiRef,
  useApi,
} from '@backstage/core-plugin-api';
import { Header, Page, Content } from '@backstage/core-components';
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Divider,
  Grid,
  IconButton,
  InputAdornment,
  Paper,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  Tabs,
  TextField,
  Tooltip,
  Typography,
  makeStyles,
} from '@material-ui/core';
import ArrowBackIcon from '@material-ui/icons/ArrowBack';
import CheckCircleIcon from '@material-ui/icons/CheckCircle';
import CancelIcon from '@material-ui/icons/Cancel';
import RefreshIcon from '@material-ui/icons/Refresh';
import OpenInNewIcon from '@material-ui/icons/OpenInNew';
import TrendingUpIcon from '@material-ui/icons/TrendingUp';
import AssessmentIcon from '@material-ui/icons/Assessment';
import StorageIcon from '@material-ui/icons/Storage';
import SearchIcon from '@material-ui/icons/Search';
import ClearIcon from '@material-ui/icons/Clear';
import DeleteIcon from '@material-ui/icons/Delete';
import WarningIcon from '@material-ui/icons/Warning';
import BugReportIcon from '@material-ui/icons/BugReport';

// ─── Types ────────────────────────────────────────────────────────────────────

type RepoSummary = {
  repoName: string;
  totalRuns: number;
  avgScore: number | null;
  bestScore: number | null;
  worstScore: number | null;
  lastRun: string;
  latestScore: number | null;
  latestPassed: boolean | null;
  latestBranch: string | null;
  latestModel: string | null;
  latestIsError: boolean;
  latestJobStatus: string | null;
};

type RunRecord = {
  id: string;
  repo_name: string;
  runner_id: string | null;
  workflow_run_number: number | null;
  jira_ticket_id: string | null;
  quality_score: number | null;
  score_passed: boolean | null;
  minimum_score: number | null;
  model_name: string | null;
  ai_provider: string | null;
  aws_cost: number | null;
  confluence_url: string | null;
  branch_name: string | null;
  pr_number: number | null;
  pr_creator: string | null;
  run_timestamp: string;
  is_error: boolean;
  error_message: string | null;
  error_step: string | null;
  job_status: string | null;
};

type Stats = {
  totalRuns: number;
  avgScore: number | null;
  totalCost: number | null;
  repoCount: number;
  passCount: number;
  errorCount: number;
};

type ModelStat = {
  modelName: string | null;
  aiProvider: string | null;
  totalRuns: number;
  avgScore: number | null;
  totalCost: number | null;
  passCount: number;
};

type TrendPoint = {
  id: string;
  repo_name: string;
  quality_score: number | null;
  score_passed: boolean | null;
  model_name: string | null;
  ai_provider: string | null;
  aws_cost: number | null;
  branch_name: string | null;
  pr_number: number | null;
  pr_creator: string | null;
  run_timestamp: string;
  workflow_run_number: number | null;
  is_error: boolean;
  error_step: string | null;
  job_status: string | null;
};

// ─── Styles ───────────────────────────────────────────────────────────────────

const useStyles = makeStyles(theme => ({
  statCard: {
    padding: theme.spacing(3),
    textAlign: 'center',
    borderRadius: 10,
    height: '100%',
  },
  statValue: {
    fontSize: '2.2rem',
    fontWeight: 800,
    lineHeight: 1.2,
    color: theme.palette.text.primary,
  },
  statLabel: {
    fontSize: '0.78rem',
    color: theme.palette.text.secondary,
    marginTop: 4,
    textTransform: 'uppercase',
    letterSpacing: '0.05em',
  },
  scoreBar: {
    display: 'inline-block',
    height: 8,
    borderRadius: 4,
    minWidth: 4,
    verticalAlign: 'middle',
  },
  trendBar: {
    display: 'inline-flex',
    alignItems: 'flex-end',
    gap: 2,
    height: 32,
  },
  trendBarItem: {
    width: 8,
    borderRadius: '2px 2px 0 0',
    cursor: 'pointer',
  },
  chip: {
    fontSize: '0.7rem',
    height: 20,
    cursor: 'default',
  },
  sectionTitle: {
    fontSize: '1rem',
    fontWeight: 700,
    marginBottom: theme.spacing(2),
    color: theme.palette.text.primary,
  },
  tableRow: {
    cursor: 'pointer',
    '&:hover': {
      backgroundColor: theme.palette.action.hover,
    },
  },
  modelChip: {
    fontSize: '0.65rem',
    height: 18,
    maxWidth: 180,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  },
  alarmPulse: {
    animation: '$pulse 1.4s ease-in-out infinite',
  },
  '@keyframes pulse': {
    '0%': { opacity: 1 },
    '50%': { opacity: 0.35 },
    '100%': { opacity: 1 },
  },
}));

// ─── Helpers ──────────────────────────────────────────────────────────────────

function scoreColor(score: number | string | null): string {
  if (score === null || score === undefined) return '#9E9E9E';
  const n = Number(score);
  if (n >= 8) return '#4CAF50';
  if (n >= 6) return '#8BC34A';
  if (n >= 4) return '#FF9800';
  return '#F44336';
}

function ScoreBadge({ score, passed }: { score: number | null; passed: boolean | null }) {
  const scoreNum = score !== null ? Number(score) : null;
  if (scoreNum === null) return <Typography variant="body2" color="textSecondary">—</Typography>;
  return (
    <Box display="flex" alignItems="center" style={{ gap: 6 }}>
      <span
        style={{
          fontWeight: 700,
          fontSize: '0.95rem',
          color: scoreColor(scoreNum),
        }}
      >
        {scoreNum.toFixed(1)}
      </span>
      {passed === true && (
        <Tooltip title="Passed minimum threshold">
          <CheckCircleIcon style={{ fontSize: 16, color: '#4CAF50' }} />
        </Tooltip>
      )}
      {passed === false && (
        <Tooltip title="Did not meet minimum threshold">
          <CancelIcon style={{ fontSize: 16, color: '#F44336' }} />
        </Tooltip>
      )}
    </Box>
  );
}

function MiniTrendBar({
  points,
  classes,
}: {
  points: TrendPoint[];
  classes: ReturnType<typeof useStyles>;
}) {
  if (!points.length) return <Typography variant="body2" color="textSecondary">—</Typography>;
  return (
    <Box className={classes.trendBar}>
      {points.map((p, i) => {
        const n = p.quality_score !== null ? Number(p.quality_score) : null;
        const pct = n !== null ? (n / 10) * 100 : 0;
        const barColor = p.is_error ? '#FF9800' : scoreColor(n);
        return (
          <Tooltip
            key={i}
            title={`Run #${p.workflow_run_number ?? i + 1}: ${p.is_error ? `ERROR (${p.error_step ?? 'unknown'})` : `${n !== null ? n.toFixed(1) : 'N/A'}/10`} · ${p.branch_name ?? ''} · ${new Date(p.run_timestamp).toLocaleDateString()}`}
          >
            <div
              className={classes.trendBarItem}
              style={{
                height: `${Math.max(4, p.is_error ? 40 : pct)}%`,
                backgroundColor: barColor,
                opacity: p.score_passed === false && !p.is_error ? 0.55 : 1,
              }}
            />
          </Tooltip>
        );
      })}
    </Box>
  );
}

function jobStatusColor(status: string | null): string {
  if (!status) return '#9E9E9E';
  switch (status.toLowerCase()) {
    case 'success': return '#4CAF50';
    case 'failure': return '#F44336';
    case 'cancelled': return '#FF9800';
    default: return '#9E9E9E';
  }
}

function JobStatusChip({ status, isError }: { status: string | null; isError: boolean }) {
  if (!status && !isError) return null;
  const label = isError && !status ? 'error' : (status ?? '—');
  const color = isError && status !== 'success' ? '#F44336' : jobStatusColor(status);
  return (
    <Chip
      label={label}
      size="small"
      style={{
        fontSize: '0.65rem',
        height: 18,
        backgroundColor: color,
        color: '#fff',
        fontWeight: 600,
      }}
    />
  );
}

function formatDate(ts: string): string {
  return new Date(ts).toLocaleString(undefined, {
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

/** Display just the repo part, stripping "owner/" prefix if present */
function shortName(repoName: string): string {
  const slash = repoName.indexOf('/');
  return slash !== -1 ? repoName.slice(slash + 1) : repoName;
}

// ─── Sub-views ────────────────────────────────────────────────────────────────

// ─── Daily Trend Chart ────────────────────────────────────────────────────────

type DailyBucket = {
  date: string;
  isoDate: string;
  total: number;
  passed: number;
  failed: number;
  errors: number;
  cost: number;
};

function buildDailyBuckets(trends: TrendPoint[], days: number): DailyBucket[] {
  const buckets: Record<string, DailyBucket> = {};
  const now = new Date();
  for (let i = days - 1; i >= 0; i--) {
    const d = new Date(now);
    d.setDate(d.getDate() - i);
    const iso = d.toISOString().slice(0, 10);
    const label = d.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
    buckets[iso] = { date: label, isoDate: iso, total: 0, passed: 0, failed: 0, errors: 0, cost: 0 };
  }
  for (const t of trends) {
    const iso = (t.run_timestamp || '').slice(0, 10);
    if (!buckets[iso]) continue;
    buckets[iso].total += 1;
    if (t.is_error) buckets[iso].errors += 1;
    else if (t.score_passed === true) buckets[iso].passed += 1;
    else if (t.score_passed === false) buckets[iso].failed += 1;
    if (t.aws_cost) buckets[iso].cost += Number(t.aws_cost);
  }
  return Object.values(buckets).sort((a, b) => a.isoDate.localeCompare(b.isoDate));
}

function DailyTrendChart({
  baseUrl,
  fetchApi,
  days,
}: {
  baseUrl: string;
  fetchApi: any;
  days: number;
}) {
  const [trends, setTrends] = useState<TrendPoint[]>([]);
  const [loading, setLoading] = useState(true);
  const [activeIdx, setActiveIdx] = useState<number | null>(null);
  const [showCost, setShowCost] = useState(false);

  useEffect(() => {
    setLoading(true);
    fetchApi.fetch(`${baseUrl}/trends?limit=200`)
      .then((r: any) => r.ok ? r.json() : [])
      .then((data: TrendPoint[]) => { setTrends(data); setLoading(false); })
      .catch(() => setLoading(false));
  }, [baseUrl, fetchApi, days]);

  const buckets = buildDailyBuckets(trends, days);

  const W = 900, H = 220, PAD = { top: 16, right: 20, bottom: 36, left: 42 };
  const chartW = W - PAD.left - PAD.right;
  const chartH = H - PAD.top - PAD.bottom;
  const maxY = showCost
    ? Math.max(...buckets.map(b => b.cost), 0.001)
    : Math.max(...buckets.map(b => b.total), 1);
  const xStep = buckets.length > 1 ? chartW / (buckets.length - 1) : chartW;
  const xOf = (i: number) => PAD.left + i * xStep;
  const yOf = (v: number) => PAD.top + chartH - (v / maxY) * chartH;

  const lineFor = (key: keyof DailyBucket, color: string) => {
    if (!buckets.length) return null;
    const pts = buckets.map((b, i) => `${xOf(i)},${yOf(Number(b[key]))}`).join(' ');
    return <polyline key={key} points={pts} fill="none" stroke={color} strokeWidth={2} strokeLinejoin="round" strokeLinecap="round" />;
  };
  const areaFor = (key: keyof DailyBucket, color: string) => {
    if (!buckets.length) return null;
    const top = buckets.map((b, i) => `${xOf(i)},${yOf(Number(b[key]))}`).join(' ');
    const base = `${xOf(buckets.length - 1)},${yOf(0)} ${xOf(0)},${yOf(0)}`;
    return <polygon key={key} points={`${top} ${base}`} fill={color} fillOpacity={0.08} />;
  };

  const yTicks = [0, 0.25, 0.5, 0.75, 1].map(f => ({
    v: f * maxY,
    y: yOf(f * maxY),
    label: showCost ? `$${(f * maxY).toFixed(2)}` : String(Math.round(f * maxY)),
  }));
  const labelStep = Math.max(1, Math.ceil(buckets.length / 10));

  if (loading) return <Box display="flex" justifyContent="center" py={4}><CircularProgress size={24} /></Box>;
  if (buckets.every(b => b.total === 0)) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" height={100}>
        <Typography variant="body2" color="textSecondary">No runs in this time range</Typography>
      </Box>
    );
  }

  const LINES = showCost
    ? [{ key: 'cost' as const, color: '#1a73e8', label: 'AWS Cost ($)' }]
    : [
        { key: 'total' as const, color: '#1a73e8', label: 'Total' },
        { key: 'passed' as const, color: '#4CAF50', label: 'Passed' },
        { key: 'failed' as const, color: '#FF9800', label: 'Failed' },
        { key: 'errors' as const, color: '#F44336', label: 'Errors' },
      ];

  const active = activeIdx !== null ? buckets[activeIdx] : null;

  return (
    <Box>
      <Box display="flex" alignItems="center" mb={1} style={{ gap: 16, flexWrap: 'wrap' }}>
        {LINES.map(l => (
          <Box key={l.key} display="flex" alignItems="center" style={{ gap: 5 }}>
            <span style={{ display: 'inline-block', width: 20, height: 3, borderRadius: 2, backgroundColor: l.color }} />
            <Typography variant="caption" color="textSecondary">{l.label}</Typography>
          </Box>
        ))}
        <Box style={{ marginLeft: 'auto' }}>
          <Chip
            label={showCost ? 'Show Runs' : 'Show Cost'}
            size="small"
            clickable
            onClick={() => setShowCost(v => !v)}
            style={{ fontSize: '0.68rem', height: 20 }}
          />
        </Box>
      </Box>

      <Box style={{ overflowX: 'auto' }}>
        <svg
          viewBox={`0 0 ${W} ${H}`}
          style={{ width: '100%', maxWidth: W, display: 'block' }}
          onMouseLeave={() => setActiveIdx(null)}
        >
          {yTicks.map((t, i) => (
            <g key={i}>
              <line x1={PAD.left} y1={t.y} x2={W - PAD.right} y2={t.y}
                stroke="#E0E0E0" strokeWidth={1} strokeDasharray={i === 0 ? '0' : '4 3'} />
              <text x={PAD.left - 6} y={t.y + 4} textAnchor="end"
                style={{ fontSize: 10, fill: '#9E9E9E', fontFamily: 'monospace' }}>
                {t.label}
              </text>
            </g>
          ))}
          {LINES.map(l => areaFor(l.key, l.color))}
          {LINES.map(l => lineFor(l.key, l.color))}
          {buckets.map((b, i) => i % labelStep === 0 && (
            <text key={i} x={xOf(i)} y={H - 6} textAnchor="middle"
              style={{ fontSize: 10, fill: '#9E9E9E', fontFamily: 'sans-serif' }}>
              {b.date}
            </text>
          ))}
          {buckets.map((_, i) => (
            <rect key={i} x={xOf(i) - Math.max(xStep, 8) / 2} y={PAD.top}
              width={Math.max(xStep, 8)} height={chartH}
              fill="transparent" onMouseEnter={() => setActiveIdx(i)} />
          ))}
          {activeIdx !== null && (
            <>
              <line x1={xOf(activeIdx)} y1={PAD.top} x2={xOf(activeIdx)} y2={PAD.top + chartH}
                stroke="#9E9E9E" strokeWidth={1} strokeDasharray="3 2" />
              {LINES.map(l => (
                <circle key={l.key} cx={xOf(activeIdx)} cy={yOf(Number(buckets[activeIdx][l.key]))}
                  r={4} fill={l.color} stroke="#fff" strokeWidth={2} />
              ))}
            </>
          )}
        </svg>
      </Box>

      <Box style={{ minHeight: 36 }}>
        {active && (
          <Box display="flex" alignItems="center" style={{ gap: 16, flexWrap: 'wrap', paddingLeft: 8 }}>
            <Typography variant="caption" style={{ fontWeight: 700, color: '#555' }}>{active.date}:</Typography>
            {showCost ? (
              <Typography variant="caption" style={{ color: '#1a73e8', fontFamily: 'monospace' }}>
                ${active.cost.toFixed(4)}
              </Typography>
            ) : (
              <>
                <Typography variant="caption" style={{ color: '#1a73e8' }}>Total: <strong>{active.total}</strong></Typography>
                <Typography variant="caption" style={{ color: '#4CAF50' }}>Passed: <strong>{active.passed}</strong></Typography>
                <Typography variant="caption" style={{ color: '#FF9800' }}>Failed: <strong>{active.failed}</strong></Typography>
                <Typography variant="caption" style={{ color: '#F44336' }}>Errors: <strong>{active.errors}</strong></Typography>
              </>
            )}
          </Box>
        )}
      </Box>
    </Box>
  );
}

function StatsPanel({ baseUrl, fetchApi }: { baseUrl: string; fetchApi: any }) {
  const classes = useStyles();
  const [stats, setStats] = useState<Stats | null>(null);
  const [modelStats, setModelStats] = useState<ModelStat[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const [sRes, mRes] = await Promise.all([
        fetchApi.fetch(`${baseUrl}/stats`),
        fetchApi.fetch(`${baseUrl}/stats/by-model`),
      ]);
      if (!sRes.ok) throw new Error(`Stats: HTTP ${sRes.status}`);
      if (!mRes.ok) throw new Error(`Model stats: HTTP ${mRes.status}`);
      setStats(await sRes.json());
      setModelStats(await mRes.json());
      setError(null);
    } catch (e: any) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, [baseUrl, fetchApi]);

  useEffect(() => { load(); }, [load]);

  if (loading) return <Box display="flex" justifyContent="center" py={6}><CircularProgress /></Box>;
  if (error) return <Box py={2}><Typography color="error">{error}</Typography></Box>;
  if (!stats) return null;

  const passRate = stats.totalRuns > 0 ? ((stats.passCount / stats.totalRuns) * 100).toFixed(0) : '—';

  return (
    <Box>
      <Grid container spacing={3} style={{ marginBottom: 24 }}>
        {[
          { label: 'Repositories', value: stats.repoCount },
          { label: 'Total Runs', value: stats.totalRuns },
          {
            label: 'Avg Quality Score',
            value: stats.avgScore !== null ? `${stats.avgScore}/10` : '—',
            color: scoreColor(stats.avgScore),
          },
          {
            label: 'Pass Rate',
            value: `${passRate}%`,
            color: Number(passRate) >= 70 ? '#4CAF50' : '#F44336',
          },
          {
            label: 'Errors',
            value: stats.errorCount,
            color: stats.errorCount > 0 ? '#F44336' : '#4CAF50',
          },
          {
            label: 'Total AWS Cost',
            value: stats.totalCost !== null ? `$${stats.totalCost.toFixed(3)}` : '—',
          },
        ].map(s => (
          <Grid item xs={6} sm={4} md={2} key={s.label}>
            <Paper className={classes.statCard} elevation={1}>
              <Typography
                className={classes.statValue}
                style={s.color ? { color: s.color } : undefined}
              >
                {String(s.value)}
              </Typography>
              <Typography className={classes.statLabel}>{s.label}</Typography>
            </Paper>
          </Grid>
        ))}
      </Grid>

      <Typography className={classes.sectionTitle}>Daily Run Trend</Typography>
      <Paper elevation={1} style={{ padding: '16px 20px', marginBottom: 24 }}>
        <DailyTrendChart baseUrl={baseUrl} fetchApi={fetchApi} days={30} />
      </Paper>

      <Typography className={classes.sectionTitle}>Stats by AI Model</Typography>
      <TableContainer component={Paper} elevation={1}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell><strong>Model</strong></TableCell>
              <TableCell><strong>Provider</strong></TableCell>
              <TableCell align="right"><strong>Runs</strong></TableCell>
              <TableCell align="right"><strong>Avg Score</strong></TableCell>
              <TableCell align="right"><strong>Pass Rate</strong></TableCell>
              <TableCell align="right"><strong>Total Cost</strong></TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {modelStats.length === 0 && (
              <TableRow>
                <TableCell colSpan={6} align="center">
                  <Typography variant="body2" color="textSecondary">No data yet</Typography>
                </TableCell>
              </TableRow>
            )}
            {modelStats.map((m, i) => (
              <TableRow key={i}>
                <TableCell>
                  <Typography variant="body2" style={{ fontFamily: 'monospace', fontSize: '0.8rem' }}>
                    {m.modelName ?? '—'}
                  </Typography>
                </TableCell>
                <TableCell>
                  <Chip
                    label={m.aiProvider ?? 'unknown'}
                    size="small"
                    style={{ fontSize: '0.68rem', height: 18 }}
                  />
                </TableCell>
                <TableCell align="right">{m.totalRuns}</TableCell>
                <TableCell align="right">
                  <span style={{ color: scoreColor(m.avgScore), fontWeight: 600 }}>
                    {m.avgScore !== null ? `${m.avgScore.toFixed(1)}/10` : '—'}
                  </span>
                </TableCell>
                <TableCell align="right">
                  {m.totalRuns > 0 ? `${((m.passCount / m.totalRuns) * 100).toFixed(0)}%` : '—'}
                </TableCell>
                <TableCell align="right">
                  {m.totalCost !== null ? `$${m.totalCost.toFixed(4)}` : '—'}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
}

// ─── Run Detail View ──────────────────────────────────────────────────────────

function RunDetailView({
  run,
  onBack,
  baseUrl,
  fetchApi,
}: {
  run: RunRecord;
  onBack: () => void;
  baseUrl: string;
  fetchApi: any;
}) {
  const [deleting, setDeleting] = useState(false);

  const handleDelete = async () => {
    setDeleting(true);
    await fetchApi.fetch(`${baseUrl}/run/${run.id}`, { method: 'DELETE' });
    onBack();
  };

  const runLabel = `#${run.workflow_run_number ?? run.runner_id?.slice(0, 8) ?? run.id.slice(0, 8)}`;

  return (
    <Box>
      {/* Header */}
      <Box display="flex" alignItems="center" mb={2} style={{ gap: 12 }}>
        <IconButton size="small" onClick={onBack}>
          <ArrowBackIcon />
        </IconButton>
        <Typography variant="h6" style={{ fontWeight: 700 }}>
          Run {runLabel}
        </Typography>
        <ScoreBadge score={run.quality_score} passed={run.score_passed} />
        {run.is_error && (
          <Chip
            label="error"
            size="small"
            style={{ fontSize: '0.68rem', height: 20, backgroundColor: '#F44336', color: '#fff', fontWeight: 600 }}
          />
        )}
        <Box style={{ flex: 1 }} />
        <Tooltip title="Delete this run">
          <IconButton size="small" disabled={deleting} onClick={handleDelete}>
            <DeleteIcon style={{ fontSize: 18, color: deleting ? '#ccc' : '#D32F2F' }} />
          </IconButton>
        </Tooltip>
      </Box>

      <Grid container spacing={2}>
        {/* Run Overview */}
        <Grid item xs={12} md={6}>
          <Paper elevation={1} style={{ padding: 20 }}>
            <Typography variant="subtitle1" style={{ fontWeight: 700, marginBottom: 12 }}>
              Run Overview
            </Typography>
            {[
              { label: 'Repository', value: run.repo_name },
              { label: 'JIRA Ticket', value: run.jira_ticket_id ?? '—' },
              { label: 'Run Number', value: run.workflow_run_number ? `#${run.workflow_run_number}` : '—' },
              { label: 'Runner ID', value: run.runner_id ?? '—' },
              { label: 'Timestamp', value: formatDate(run.run_timestamp) },
              { label: 'Branch', value: run.branch_name ?? '—' },
              {
                label: 'PR Creator',
                value: run.pr_creator ? (
                  <a
                    href={`/catalog/default/user/${run.pr_creator}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{ textDecoration: 'none', color: 'inherit' }}
                    onMouseOver={e => { (e.currentTarget as HTMLAnchorElement).style.textDecoration = 'underline'; }}
                    onMouseOut={e => { (e.currentTarget as HTMLAnchorElement).style.textDecoration = 'none'; }}
                  >
                    {run.pr_creator}
                  </a>
                ) : '—',
              },
            ].map(({ label, value }) => (
              <Box key={label} display="flex" style={{ marginBottom: 6 }}>
                <Typography variant="body2" color="textSecondary" style={{ minWidth: 130 }}>{label}:</Typography>
                <Typography variant="body2" style={{ fontWeight: 500 }}>{value}</Typography>
              </Box>
            ))}
          </Paper>
        </Grid>

        {/* Quality Score */}
        <Grid item xs={12} md={6}>
          <Paper elevation={1} style={{ padding: 20 }}>
            <Typography variant="subtitle1" style={{ fontWeight: 700, marginBottom: 12 }}>
              Quality Score
            </Typography>
            <Box display="flex" style={{ marginBottom: 10 }}>
              <Typography variant="body2" color="textSecondary" style={{ minWidth: 130 }}>Score:</Typography>
              <ScoreBadge score={run.quality_score} passed={run.score_passed} />
            </Box>
            {[
              { label: 'Minimum Score', value: run.minimum_score !== null ? `${run.minimum_score}/10` : '—' },
              { label: 'Result', value: run.score_passed === true ? 'Passed ✓' : run.score_passed === false ? 'Failed ✗' : '—' },
            ].map(({ label, value }) => (
              <Box key={label} display="flex" style={{ marginBottom: 6 }}>
                <Typography variant="body2" color="textSecondary" style={{ minWidth: 130 }}>{label}:</Typography>
                <Typography variant="body2" style={{
                  fontWeight: 500,
                  color: label === 'Result' ? (run.score_passed === true ? '#4CAF50' : run.score_passed === false ? '#F44336' : 'inherit') : 'inherit',
                }}>
                  {value}
                </Typography>
              </Box>
            ))}
            {/* Score bar */}
            {run.quality_score !== null && (
              <Box mt={2}>
                <Box style={{ background: '#F5F5F5', borderRadius: 4, height: 8, overflow: 'hidden' }}>
                  <Box style={{
                    width: `${(Number(run.quality_score) / 10) * 100}%`,
                    height: '100%',
                    backgroundColor: scoreColor(run.quality_score),
                    borderRadius: 4,
                    transition: 'width 0.4s ease',
                  }} />
                </Box>
                <Typography variant="caption" color="textSecondary" style={{ marginTop: 4, display: 'block' }}>
                  {Number(run.quality_score).toFixed(1)} / 10
                </Typography>
              </Box>
            )}
          </Paper>
        </Grid>

        {/* AI Usage */}
        <Grid item xs={12} md={6}>
          <Paper elevation={1} style={{ padding: 20 }}>
            <Typography variant="subtitle1" style={{ fontWeight: 700, marginBottom: 12 }}>
              AI Usage
            </Typography>
            {[
              { label: 'Model', value: run.model_name },
              { label: 'Provider', value: run.ai_provider },
              { label: 'AWS Cost', value: run.aws_cost !== null ? `$${Number(run.aws_cost).toFixed(4)}` : null },
            ].map(({ label, value }) => (
              <Box key={label} display="flex" style={{ marginBottom: 6 }}>
                <Typography variant="body2" color="textSecondary" style={{ minWidth: 130 }}>{label}:</Typography>
                <Typography variant="body2" style={{ fontFamily: 'monospace', fontWeight: 500 }}>{value ?? '—'}</Typography>
              </Box>
            ))}
          </Paper>
        </Grid>

        {/* Links */}
        <Grid item xs={12} md={6}>
          <Paper elevation={1} style={{ padding: 20 }}>
            <Typography variant="subtitle1" style={{ fontWeight: 700, marginBottom: 12 }}>
              Links
            </Typography>
            {[
              {
                label: 'GitHub Branch',
                href: run.branch_name ? `https://github.com/${run.repo_name}/tree/${run.branch_name}` : null,
              },
              {
                label: 'GitHub PR',
                href: run.pr_number ? `https://github.com/${run.repo_name}/pull/${run.pr_number}` : null,
              },
              { label: 'Confluence Report', href: run.confluence_url },
            ].filter(l => l.href).map(({ label, href }) => (
              <Box key={label} mb={1}>
                <a href={href!} target="_blank" rel="noopener noreferrer"
                  style={{ display: 'inline-flex', alignItems: 'center', gap: 4, fontSize: '0.875rem' }}>
                  {label} <OpenInNewIcon style={{ fontSize: 13 }} />
                </a>
              </Box>
            ))}
            {!run.branch_name && !run.pr_number && !run.confluence_url && (
              <Typography variant="body2" color="textSecondary">No links available</Typography>
            )}
          </Paper>
        </Grid>

        {/* Error Details */}
        {run.is_error && (
          <Grid item xs={12}>
            <Paper elevation={1} style={{ padding: 20, borderLeft: '4px solid #D32F2F' }}>
              <Box display="flex" alignItems="center" style={{ gap: 8, marginBottom: 12 }}>
                <BugReportIcon style={{ color: '#D32F2F' }} />
                <Typography variant="subtitle1" style={{ fontWeight: 700, color: '#B71C1C' }}>
                  Error Details
                </Typography>
              </Box>
              {run.error_step && (
                <Box display="flex" style={{ marginBottom: 8 }}>
                  <Typography variant="body2" color="textSecondary" style={{ minWidth: 130 }}>Step:</Typography>
                  <Chip label={run.error_step} size="small"
                    style={{ fontSize: '0.68rem', height: 20, backgroundColor: '#FFF3E0', color: '#E65100', border: '1px solid #FFB74D' }} />
                </Box>
              )}
              {run.job_status && (
                <Box display="flex" style={{ marginBottom: 8 }}>
                  <Typography variant="body2" color="textSecondary" style={{ minWidth: 130 }}>Job Status:</Typography>
                  <Typography variant="body2" style={{ fontWeight: 500 }}>{run.job_status}</Typography>
                </Box>
              )}
              {run.error_message && (
                <Box>
                  <Typography variant="body2" color="textSecondary" style={{ marginBottom: 4 }}>Message:</Typography>
                  <Box
                    component="pre"
                    style={{
                      background: '#FFF8F8',
                      border: '1px solid #FFCDD2',
                      borderRadius: 6,
                      padding: 12,
                      fontSize: '0.8rem',
                      color: '#D32F2F',
                      whiteSpace: 'pre-wrap',
                      wordBreak: 'break-word',
                      margin: 0,
                    }}
                  >
                    {run.error_message}
                  </Box>
                </Box>
              )}
              <Box mt={2} p={1.5} style={{ background: '#F3F4F6', borderRadius: 6 }}>
                <Typography variant="caption" color="textSecondary">
                  <strong>Troubleshooting tips:</strong> Check the GitHub Actions logs for run {runLabel}.
                  Common causes: AI API timeouts, insufficient test coverage data, or repository access issues.
                </Typography>
              </Box>
            </Paper>
          </Grid>
        )}
      </Grid>
    </Box>
  );
}

function RepoHistoryPanel({
  repoName,
  baseUrl,
  fetchApi,
  discoveryApi,
  onBack,
}: {
  repoName: string;
  baseUrl: string;
  fetchApi: any;
  discoveryApi: any;
  onBack: () => void;
}) {
  const [runs, setRuns] = useState<RunRecord[]>([]);
  const [trends, setTrends] = useState<TrendPoint[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState<'all' | 'passed' | 'failed' | 'error'>('all');
  const [confirmDeleteRepo, setConfirmDeleteRepo] = useState(false);
  const [deletingRunId, setDeletingRunId] = useState<string | null>(null);
  const [subTab, setSubTab] = useState(0);
  const [jiraUrl, setJiraUrl] = useState<string | null>(null);
  const [selectedRun, setSelectedRun] = useState<RunRecord | null>(null);
  const classes = useStyles();

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const [rRes, tRes] = await Promise.all([
        fetchApi.fetch(`${baseUrl}/repo/${encodeURIComponent(repoName)}?limit=10`),
        fetchApi.fetch(`${baseUrl}/trends?repoName=${encodeURIComponent(repoName)}&limit=20`),
      ]);
      if (!rRes.ok) throw new Error(`HTTP ${rRes.status}`);
      setRuns(await rRes.json());
      if (tRes.ok) setTrends(await tRes.json());
      setError(null);
    } catch (e: any) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, [repoName, baseUrl, fetchApi]);

  useEffect(() => { load(); }, [load]);

  // Fetch Jira URL for this repo from jira-settings plugin
  useEffect(() => {
    const repoSlug = repoName.includes('/') ? repoName.split('/')[1] : repoName;
    const entityRef = `component:default/${repoSlug}`;
    discoveryApi.getBaseUrl('jira-settings').then((jiraBase: string) => {
      fetchApi.fetch(`${jiraBase}/?entityRef=${encodeURIComponent(entityRef)}`)
        .then((r: any) => r.ok ? r.json() : [])
        .then((configs: any[]) => {
          if (configs.length > 0 && configs[0].jira_url) {
            setJiraUrl(configs[0].jira_url.replace(/\/$/, ''));
          }
        })
        .catch(() => {/* silently ignore */});
    });
  }, [repoName, discoveryApi, fetchApi]);

  const handleDeleteRepo = async () => {
    await fetchApi.fetch(`${baseUrl}/repo/${encodeURIComponent(repoName)}`, { method: 'DELETE' });
    onBack();
  };

  const handleDeleteRun = async (id: string) => {
    setDeletingRunId(id);
    await fetchApi.fetch(`${baseUrl}/run/${id}`, { method: 'DELETE' });
    setRuns(prev => prev.filter(r => r.id !== id));
    setDeletingRunId(null);
  };

  if (selectedRun) {
    return (
      <RunDetailView
        run={selectedRun}
        onBack={() => setSelectedRun(null)}
        baseUrl={baseUrl}
        fetchApi={fetchApi}
      />
    );
  }

  return (
    <Box>
      <Box display="flex" alignItems="center" mb={2} style={{ gap: 12 }}>
        <IconButton size="small" onClick={onBack}>
          <ArrowBackIcon />
        </IconButton>
        <Typography variant="h6" style={{ fontWeight: 700 }}>
          {shortName(repoName)}
        </Typography>
        <IconButton size="small" onClick={load} title="Refresh">
          <RefreshIcon />
        </IconButton>
        <Box style={{ flex: 1 }} />
        {confirmDeleteRepo ? (
          <Box display="flex" alignItems="center" style={{ gap: 8 }}>
            <Typography variant="body2" color="error">Delete all runs for this repo?</Typography>
            <Button size="small" variant="contained" style={{ backgroundColor: '#D32F2F', color: '#fff' }} onClick={handleDeleteRepo}>
              Confirm
            </Button>
            <Button size="small" onClick={() => setConfirmDeleteRepo(false)}>Cancel</Button>
          </Box>
        ) : (
          <Tooltip title="Delete all runs for this repo">
            <IconButton size="small" onClick={() => setConfirmDeleteRepo(true)}>
              <DeleteIcon style={{ color: '#D32F2F' }} />
            </IconButton>
          </Tooltip>
        )}
      </Box>

      {loading && <Box display="flex" justifyContent="center" py={6}><CircularProgress /></Box>}
      {error && <Typography color="error">{error}</Typography>}

      {!loading && !error && (
        <>
          {/* Active error alarm banner */}
          {runs.length > 0 && runs[0].is_error && (
            <Paper
              elevation={0}
              style={{
                background: '#FFEBEE',
                border: '1px solid #FFCDD2',
                borderRadius: 8,
                padding: '10px 16px',
                marginBottom: 16,
                display: 'flex',
                alignItems: 'center',
                gap: 10,
              }}
            >
              <WarningIcon style={{ color: '#D32F2F', fontSize: 20 }} />
              <Box>
                <Typography variant="body2" style={{ fontWeight: 700, color: '#B71C1C' }}>
                  Active Error — last run failed
                </Typography>
                <Typography variant="caption" color="textSecondary">
                  {runs[0].error_step ? `Step: ${runs[0].error_step} · ` : ''}
                  {runs[0].error_message ?? 'No error message recorded'}
                </Typography>
              </Box>
            </Paper>
          )}

          {/* Score trend sparkline */}
          {trends.length > 0 && (
            <Box mb={3}>
              <Typography className={classes.sectionTitle}>Score Trend (last {trends.length} runs)</Typography>
              <Paper elevation={1} style={{ padding: 16 }}>
                <Box display="flex" alignItems="flex-end" style={{ gap: 4, height: 60 }}>
                  {trends.map((p, i) => {
                    const n = p.quality_score !== null ? Number(p.quality_score) : null;
                    const pct = n !== null ? (n / 10) * 100 : 5;
                    return (
                      <Tooltip
                        key={i}
                        title={`${n !== null ? n.toFixed(1) : 'N/A'}/10 · ${p.branch_name ?? 'unknown'} · ${formatDate(p.run_timestamp)}`}
                      >
                        <Box
                          style={{
                            flex: 1,
                            maxWidth: 28,
                            height: `${Math.max(6, pct)}%`,
                            backgroundColor: scoreColor(p.quality_score),
                            borderRadius: '3px 3px 0 0',
                            opacity: p.score_passed === false ? 0.5 : 1,
                            cursor: 'default',
                          }}
                        />
                      </Tooltip>
                    );
                  })}
                </Box>
                <Box display="flex" justifyContent="space-between" mt={0.5}>
                  <Typography variant="caption" color="textSecondary">
                    {trends.length > 0 ? formatDate(trends[0].run_timestamp) : ''}
                  </Typography>
                  <Typography variant="caption" color="textSecondary">
                    {trends.length > 0 ? formatDate(trends[trends.length - 1].run_timestamp) : ''}
                  </Typography>
                </Box>
              </Paper>
            </Box>
          )}

          {/* Sub-tabs: Quality Runs vs Errors */}
          {(() => {
            const qualityRuns = runs.filter(r => !r.is_error);
            const errorRuns = runs.filter(r => r.is_error);
            const q = search.trim().toLowerCase();

            const filteredQuality = qualityRuns.filter(r => !q ||
              (r.branch_name ?? '').toLowerCase().includes(q) ||
              (r.model_name ?? '').toLowerCase().includes(q) ||
              (r.jira_ticket_id ?? '').toLowerCase().includes(q) ||
              String(r.pr_number ?? '').includes(q) ||
              String(r.workflow_run_number ?? '').includes(q),
            ).filter(r =>
              statusFilter === 'all' ||
              (statusFilter === 'passed' && r.score_passed === true) ||
              (statusFilter === 'failed' && r.score_passed === false),
            );

            const filteredErrors = errorRuns.filter(r => !q ||
              (r.branch_name ?? '').toLowerCase().includes(q) ||
              (r.error_step ?? '').toLowerCase().includes(q) ||
              (r.error_message ?? '').toLowerCase().includes(q) ||
              String(r.workflow_run_number ?? '').includes(q),
            );

            return (
              <>
                {/* Toolbar */}
                <Box display="flex" alignItems="center" mb={1} style={{ gap: 12, flexWrap: 'wrap' }}>
                  <Tabs
                    value={subTab}
                    onChange={(_, v) => setSubTab(v)}
                    indicatorColor="primary"
                    textColor="primary"
                  >
                    <Tab label={`Quality Runs (${qualityRuns.length})`} style={{ minWidth: 0, fontSize: '0.82rem' }} />
                    <Tab
                      label={`Errors (${errorRuns.length})`}
                      style={{ minWidth: 0, fontSize: '0.82rem', color: errorRuns.length > 0 ? '#D32F2F' : undefined }}
                    />
                  </Tabs>
                  <Box style={{ flex: 1, minWidth: 180 }}>
                    <TextField
                      size="small"
                      variant="outlined"
                      placeholder={subTab === 0 ? 'Search branch, model, Jira, PR#…' : 'Search branch, step, message…'}
                      value={search}
                      onChange={e => setSearch(e.target.value)}
                      fullWidth
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <SearchIcon style={{ fontSize: 18, color: '#999' }} />
                          </InputAdornment>
                        ),
                        endAdornment: search ? (
                          <InputAdornment position="end">
                            <IconButton size="small" onClick={() => setSearch('')}>
                              <ClearIcon style={{ fontSize: 16 }} />
                            </IconButton>
                          </InputAdornment>
                        ) : undefined,
                      }}
                    />
                  </Box>
                  {subTab === 0 && (
                    <Box display="flex" style={{ gap: 6 }}>
                      {(['all', 'passed', 'failed'] as const).map(f => (
                        <Chip
                          key={f}
                          label={f.charAt(0).toUpperCase() + f.slice(1)}
                          size="small"
                          clickable
                          onClick={() => setStatusFilter(f)}
                          style={{
                            fontWeight: statusFilter === f ? 700 : 400,
                            backgroundColor: statusFilter === f
                              ? f === 'passed' ? '#4CAF50' : f === 'failed' ? '#F44336' : '#1a73e8'
                              : undefined,
                            color: statusFilter === f ? '#fff' : undefined,
                          }}
                        />
                      ))}
                    </Box>
                  )}
                </Box>

                {/* Quality Runs table */}
                {subTab === 0 && (
                  <TableContainer component={Paper} elevation={1}>
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell><strong>Run</strong></TableCell>
                          <TableCell><strong>Score</strong></TableCell>
                          <TableCell><strong>Branch</strong></TableCell>
                          <TableCell><strong>PR</strong></TableCell>
                          <TableCell><strong>PR Creator</strong></TableCell>
                          <TableCell><strong>Model</strong></TableCell>
                          <TableCell><strong>AWS Cost</strong></TableCell>
                          <TableCell><strong>Timestamp</strong></TableCell>
                          <TableCell />
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {filteredQuality.length === 0 && (
                          <TableRow>
                            <TableCell colSpan={9} align="center">
                              <Typography variant="body2" color="textSecondary">
                                {qualityRuns.length === 0 ? 'No quality runs recorded yet' : 'No runs match your search'}
                              </Typography>
                            </TableCell>
                          </TableRow>
                        )}
                        {filteredQuality.map(r => (
                          <TableRow key={r.id} className={classes.tableRow} onClick={() => setSelectedRun(r)}>
                            <TableCell>
                              <Typography variant="body2" style={{ fontFamily: 'monospace', fontSize: '0.78rem' }}>
                                #{r.workflow_run_number ?? r.runner_id?.slice(0, 8) ?? '—'}
                              </Typography>
                            </TableCell>
                            <TableCell>
                              <ScoreBadge score={r.quality_score} passed={r.score_passed} />
                            </TableCell>
                            <TableCell>
                              {r.branch_name ? (
                                <a
                                  href={`https://github.com/${r.repo_name}/tree/${r.branch_name}`}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  style={{ fontFamily: 'monospace', fontSize: '0.78rem' }}
                                >
                                  {r.branch_name}
                                </a>
                              ) : '—'}
                            </TableCell>
                            <TableCell>
                              {r.pr_number ? (
                                <a
                                  href={`https://github.com/${r.repo_name}/pull/${r.pr_number}`}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  style={{ fontFamily: 'monospace', fontSize: '0.78rem' }}
                                >
                                  #{r.pr_number}
                                </a>
                              ) : '—'}
                            </TableCell>
                            <TableCell>
                              <Typography variant="body2" style={{ fontSize: '0.78rem' }}>
                                {r.pr_creator ? (
                                  <a
                                    href={`/catalog/default/user/${r.pr_creator}`}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    style={{ textDecoration: 'none', color: 'inherit' }}
                                    onMouseOver={e => { (e.currentTarget as HTMLAnchorElement).style.textDecoration = 'underline'; }}
                                    onMouseOut={e => { (e.currentTarget as HTMLAnchorElement).style.textDecoration = 'none'; }}
                                  >
                                    {r.pr_creator}
                                  </a>
                                ) : '—'}
                              </Typography>
                            </TableCell>
                            <TableCell>
                              <Tooltip title={`${r.model_name ?? ''}${r.ai_provider ? ` (${r.ai_provider})` : ''}`}>
                                <Typography variant="body2" style={{ fontFamily: 'monospace', fontSize: '0.72rem', maxWidth: 160, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                  {r.model_name ?? '—'}
                                </Typography>
                              </Tooltip>
                            </TableCell>
                            <TableCell>
                              <Typography variant="body2" style={{ fontFamily: 'monospace', fontSize: '0.78rem' }}>
                                {r.aws_cost !== null ? `$${Number(r.aws_cost).toFixed(4)}` : '—'}
                              </Typography>
                            </TableCell>
                            <TableCell>
                              <Typography variant="body2" color="textSecondary" style={{ whiteSpace: 'nowrap' }}>
                                {formatDate(r.run_timestamp)}
                              </Typography>
                            </TableCell>
                            <TableCell padding="none" onClick={e => e.stopPropagation()}>
                              <Tooltip title="Delete this run">
                                <IconButton size="small" disabled={deletingRunId === r.id} onClick={e => { e.stopPropagation(); handleDeleteRun(r.id); }}>
                                  <DeleteIcon style={{ fontSize: 16, color: deletingRunId === r.id ? '#ccc' : '#D32F2F' }} />
                                </IconButton>
                              </Tooltip>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                )}

                {/* Errors tab — card layout so message is fully visible */}
                {subTab === 1 && (
                  <Box>
                    {filteredErrors.length === 0 && (
                      <Paper elevation={1} style={{ padding: 24, textAlign: 'center' }}>
                        <Typography variant="body2" color="textSecondary">
                          {errorRuns.length === 0 ? 'No errors recorded' : 'No errors match your search'}
                        </Typography>
                      </Paper>
                    )}
                    {filteredErrors.map(r => (
                      <Paper
                        key={r.id}
                        elevation={1}
                        style={{ padding: '12px 16px', marginBottom: 10, borderLeft: '4px solid #D32F2F', cursor: 'pointer' }}
                        onClick={() => setSelectedRun(r)}
                      >
                        <Box display="flex" alignItems="flex-start" style={{ gap: 12 }}>
                          <Box style={{ flex: 1 }}>
                            {/* Top row: run#, branch, timestamp */}
                            <Box display="flex" alignItems="center" style={{ gap: 8, flexWrap: 'wrap', marginBottom: 6 }}>
                              <Typography variant="body2" style={{ fontFamily: 'monospace', fontWeight: 700, fontSize: '0.82rem' }}>
                                #{r.workflow_run_number ?? r.runner_id?.slice(0, 8) ?? '—'}
                              </Typography>
                              {r.branch_name && (
                                <Chip
                                  label={r.branch_name}
                                  size="small"
                                  style={{ fontSize: '0.65rem', height: 18, fontFamily: 'monospace' }}
                                />
                              )}
                              {r.pr_number && (
                                <Typography variant="caption" style={{ fontFamily: 'monospace' }}>PR #{r.pr_number}</Typography>
                              )}
                              <Typography variant="caption" color="textSecondary" style={{ marginLeft: 'auto' }}>
                                {formatDate(r.run_timestamp)}
                              </Typography>
                            </Box>

                            {/* Error step */}
                            {r.error_step && (
                              <Box display="flex" alignItems="center" style={{ gap: 6, marginBottom: 6 }}>
                                <Typography variant="caption" color="textSecondary" style={{ minWidth: 70 }}>Step:</Typography>
                                <Chip
                                  label={r.error_step}
                                  size="small"
                                  style={{ fontSize: '0.68rem', height: 20, backgroundColor: '#FFF3E0', color: '#E65100', border: '1px solid #FFB74D' }}
                                />
                              </Box>
                            )}

                            {/* Full error message */}
                            {r.error_message && (
                              <Box display="flex" alignItems="flex-start" style={{ gap: 6 }}>
                                <Typography variant="caption" color="textSecondary" style={{ minWidth: 70, paddingTop: 2 }}>Message:</Typography>
                                <Typography
                                  variant="body2"
                                  style={{
                                    fontSize: '0.8rem',
                                    color: '#D32F2F',
                                    whiteSpace: 'pre-wrap',
                                    wordBreak: 'break-word',
                                    flex: 1,
                                  }}
                                >
                                  {r.error_message}
                                </Typography>
                              </Box>
                            )}
                          </Box>

                          {/* Delete button */}
                          <Tooltip title="Delete this record">
                            <IconButton size="small" disabled={deletingRunId === r.id} onClick={e => { e.stopPropagation(); handleDeleteRun(r.id); }}>
                              <DeleteIcon style={{ fontSize: 16, color: deletingRunId === r.id ? '#ccc' : '#D32F2F' }} />
                            </IconButton>
                          </Tooltip>
                        </Box>
                      </Paper>
                    ))}
                  </Box>
                )}
              </>
            );
          })()}
        </>
      )}
    </Box>
  );
}

function ReposPanel({
  baseUrl,
  fetchApi,
  onSelectRepo,
}: {
  baseUrl: string;
  fetchApi: any;
  onSelectRepo: (repo: string) => void;
}) {
  const classes = useStyles();
  const [repos, setRepos] = useState<RepoSummary[]>([]);
  const [trends, setTrends] = useState<Record<string, TrendPoint[]>>({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState<'all' | 'passed' | 'failed' | 'error'>('all');
  const [confirmDeleteRepo, setConfirmDeleteRepo] = useState<string | null>(null);
  const [sortBy, setSortBy] = useState<'latestScore' | 'avgScore' | 'bestScore' | 'totalRuns' | 'lastRun' | null>(null);
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('desc');

  const handleSort = (col: 'latestScore' | 'avgScore' | 'bestScore' | 'totalRuns' | 'lastRun') => {
    if (sortBy === col) {
      setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(col);
      setSortDir('desc');
    }
  };

  const handleDeleteRepo = async (repoName: string) => {
    await fetchApi.fetch(`${baseUrl}/repo/${encodeURIComponent(repoName)}`, { method: 'DELETE' });
    setRepos(prev => prev.filter(r => r.repoName !== repoName));
    setConfirmDeleteRepo(null);
  };

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const res = await fetchApi.fetch(`${baseUrl}/repos`);
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data: RepoSummary[] = await res.json();
      setRepos(data);

      // Load trend data for all repos
      const trendRes = await fetchApi.fetch(`${baseUrl}/trends?limit=200`);
      if (trendRes.ok) {
        const allTrends: TrendPoint[] = await trendRes.json();
        const grouped: Record<string, TrendPoint[]> = {};
        for (const t of allTrends) {
          if (!grouped[t.repo_name]) grouped[t.repo_name] = [];
          grouped[t.repo_name].push(t);
        }
        setTrends(grouped);
      }

      setError(null);
    } catch (e: any) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, [baseUrl, fetchApi]);

  useEffect(() => { load(); }, [load]);

  if (loading) return <Box display="flex" justifyContent="center" py={6}><CircularProgress /></Box>;
  if (error) return <Box py={2}><Typography color="error">{error}</Typography></Box>;

  const q = search.trim().toLowerCase();
  const filtered = repos.filter(r => {
    const matchesSearch = !q ||
      r.repoName.toLowerCase().includes(q) ||
      (r.latestBranch ?? '').toLowerCase().includes(q) ||
      (r.latestModel ?? '').toLowerCase().includes(q);
    const matchesStatus =
      statusFilter === 'all' ||
      (statusFilter === 'passed' && r.latestPassed === true && !r.latestIsError) ||
      (statusFilter === 'failed' && r.latestPassed === false && !r.latestIsError) ||
      (statusFilter === 'error' && r.latestIsError === true);
    return matchesSearch && matchesStatus;
  }).sort((a, b) => {
    if (sortBy) {
      const aVal = a[sortBy] ?? (sortDir === 'asc' ? Infinity : -Infinity);
      const bVal = b[sortBy] ?? (sortDir === 'asc' ? Infinity : -Infinity);
      if (sortBy === 'lastRun') {
        return sortDir === 'asc'
          ? new Date(aVal as string).getTime() - new Date(bVal as string).getTime()
          : new Date(bVal as string).getTime() - new Date(aVal as string).getTime();
      }
      return sortDir === 'asc' ? (aVal as number) - (bVal as number) : (bVal as number) - (aVal as number);
    }
    return shortName(a.repoName).localeCompare(shortName(b.repoName));
  });

  return (
    <Box>
      {/* Toolbar: title + search + filters + refresh */}
      <Box display="flex" alignItems="center" mb={2} style={{ gap: 12, flexWrap: 'wrap' }}>
        <Typography className={classes.sectionTitle} style={{ marginBottom: 0, flex: '0 0 auto' }}>
          Tracked Repositories ({filtered.length}{filtered.length !== repos.length ? ` of ${repos.length}` : ''})
        </Typography>

        <Box style={{ flex: 1, minWidth: 200 }}>
          <TextField
            size="small"
            variant="outlined"
            placeholder="Search repo, branch, model…"
            value={search}
            onChange={e => setSearch(e.target.value)}
            fullWidth
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon style={{ fontSize: 18, color: '#999' }} />
                </InputAdornment>
              ),
              endAdornment: search ? (
                <InputAdornment position="end">
                  <IconButton size="small" onClick={() => setSearch('')}>
                    <ClearIcon style={{ fontSize: 16 }} />
                  </IconButton>
                </InputAdornment>
              ) : undefined,
            }}
          />
        </Box>

        <Box display="flex" style={{ gap: 6 }}>
          {(['all', 'passed', 'failed', 'error'] as const).map(f => (
            <Chip
              key={f}
              label={f.charAt(0).toUpperCase() + f.slice(1)}
              size="small"
              clickable
              onClick={() => setStatusFilter(f)}
              style={{
                fontWeight: statusFilter === f ? 700 : 400,
                backgroundColor: statusFilter === f
                  ? f === 'passed' ? '#4CAF50'
                  : f === 'failed' ? '#F44336'
                  : f === 'error' ? '#FF9800'
                  : '#1a73e8'
                  : undefined,
                color: statusFilter === f ? '#fff' : undefined,
              }}
            />
          ))}
        </Box>

        <IconButton size="small" onClick={load} title="Refresh">
          <RefreshIcon />
        </IconButton>
      </Box>

      {repos.length === 0 && (
        <Paper elevation={1} style={{ padding: 32, textAlign: 'center' }}>
          <Typography color="textSecondary">
            No repositories tracked yet. Send a POST to{' '}
            <code>/api/test-quality-tracker/record</code> from your GitHub Actions workflow.
          </Typography>
        </Paper>
      )}

      {repos.length > 0 && filtered.length === 0 && (
        <Paper elevation={1} style={{ padding: 24, textAlign: 'center' }}>
          <Typography color="textSecondary">
            No repositories match <strong>"{search}"</strong>
            {statusFilter !== 'all' ? ` with status "${statusFilter}"` : ''}.
          </Typography>
        </Paper>
      )}

      {repos.length > 0 && filtered.length > 0 && (
        <TableContainer component={Paper} elevation={1}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell><strong>Repository</strong></TableCell>
                <TableCell sortDirection={sortBy === 'latestScore' ? sortDir : false}>
                  <TableSortLabel active={sortBy === 'latestScore'} direction={sortBy === 'latestScore' ? sortDir : 'desc'} onClick={() => handleSort('latestScore')}>
                    <strong>Latest Score</strong>
                  </TableSortLabel>
                </TableCell>
                <TableCell sortDirection={sortBy === 'avgScore' ? sortDir : false}>
                  <TableSortLabel active={sortBy === 'avgScore'} direction={sortBy === 'avgScore' ? sortDir : 'desc'} onClick={() => handleSort('avgScore')}>
                    <strong>Avg Score</strong>
                  </TableSortLabel>
                </TableCell>
                <TableCell sortDirection={sortBy === 'bestScore' ? sortDir : false}>
                  <TableSortLabel active={sortBy === 'bestScore'} direction={sortBy === 'bestScore' ? sortDir : 'desc'} onClick={() => handleSort('bestScore')}>
                    <strong>Best</strong>
                  </TableSortLabel>
                </TableCell>
                <TableCell sortDirection={sortBy === 'totalRuns' ? sortDir : false}>
                  <TableSortLabel active={sortBy === 'totalRuns'} direction={sortBy === 'totalRuns' ? sortDir : 'desc'} onClick={() => handleSort('totalRuns')}>
                    <strong>Runs</strong>
                  </TableSortLabel>
                </TableCell>
                <TableCell><strong>Score Trend</strong></TableCell>
                <TableCell sortDirection={sortBy === 'lastRun' ? sortDir : false}>
                  <TableSortLabel active={sortBy === 'lastRun'} direction={sortBy === 'lastRun' ? sortDir : 'desc'} onClick={() => handleSort('lastRun')}>
                    <strong>Last Run</strong>
                  </TableSortLabel>
                </TableCell>
                <TableCell />
              </TableRow>
            </TableHead>
            <TableBody>
              {filtered.map(r => (
                <TableRow
                  key={r.repoName}
                  className={classes.tableRow}
                  onClick={() => onSelectRepo(r.repoName)}
                >
                  <TableCell>
                    <Box display="flex" alignItems="center" style={{ gap: 6 }}>
                      {r.latestIsError && (
                        <Tooltip title="Active error — last run failed. Resolves automatically when next run succeeds.">
                          <WarningIcon className={classes.alarmPulse} style={{ fontSize: 16, color: '#D32F2F' }} />
                        </Tooltip>
                      )}
                      <Tooltip title={shortName(r.repoName)}>
                        <Typography
                          variant="body2"
                          style={{ fontWeight: 600, color: '#0469E3', cursor: 'pointer' }}
                        >
                          {shortName(r.repoName)}
                        </Typography>
                      </Tooltip>
                    </Box>
                  </TableCell>
                  <TableCell>
                    <ScoreBadge score={r.latestScore} passed={r.latestPassed} />
                  </TableCell>
                  <TableCell>
                    <span style={{ color: scoreColor(r.avgScore), fontWeight: 600 }}>
                      {r.avgScore !== null ? `${r.avgScore.toFixed(1)}/10` : '—'}
                    </span>
                  </TableCell>
                  <TableCell>
                    <span style={{ color: scoreColor(r.bestScore), fontWeight: 600 }}>
                      {r.bestScore !== null ? r.bestScore.toFixed(1) : '—'}
                    </span>
                  </TableCell>
                  <TableCell>{r.totalRuns}</TableCell>
                  <TableCell>
                    <MiniTrendBar
                      points={(trends[r.repoName] ?? []).slice(-10)}
                      classes={classes}
                    />
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" color="textSecondary" style={{ whiteSpace: 'nowrap' }}>
                      {formatDate(r.lastRun)}
                    </Typography>
                  </TableCell>
                  <TableCell padding="none" onClick={e => e.stopPropagation()}>
                    {confirmDeleteRepo === r.repoName ? (
                      <Box display="flex" alignItems="center" style={{ gap: 4, whiteSpace: 'nowrap' }}>
                        <Button size="small" variant="contained" style={{ backgroundColor: '#D32F2F', color: '#fff', fontSize: '0.65rem', padding: '2px 6px', minWidth: 0 }} onClick={() => handleDeleteRepo(r.repoName)}>
                          Delete
                        </Button>
                        <Button size="small" style={{ fontSize: '0.65rem', padding: '2px 6px', minWidth: 0 }} onClick={() => setConfirmDeleteRepo(null)}>
                          Cancel
                        </Button>
                      </Box>
                    ) : (
                      <Tooltip title="Delete repo and all its runs">
                        <IconButton size="small" onClick={() => setConfirmDeleteRepo(r.repoName)}>
                          <DeleteIcon style={{ fontSize: 16, color: '#D32F2F' }} />
                        </IconButton>
                      </Tooltip>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </Box>
  );
}

// ─── API Docs Panel ───────────────────────────────────────────────────────────

function ApiDocsPanel({ baseUrl }: { baseUrl: string }) {
  const endpoint = baseUrl.replace(/\/api\/.*/, '') + '/api/test-quality-tracker';
  return (
    <Box>
      <Typography variant="h6" style={{ fontWeight: 700, marginBottom: 16 }}>
        Webhook Integration
      </Typography>
      <Paper elevation={1} style={{ padding: 20, marginBottom: 16 }}>
        <Typography variant="subtitle2" style={{ fontWeight: 700, marginBottom: 8 }}>
          POST {endpoint}/record
        </Typography>
        <Typography variant="body2" color="textSecondary" style={{ marginBottom: 12 }}>
          Call this endpoint from your GitHub Actions workflow after running the{' '}
          <code>sourcefuse/ai-test-quality-analyzer</code> action.
          Only GitHub Actions runner IPs and <code>sf-portal.sourcef.us</code> are allowed.
        </Typography>
        <Divider style={{ marginBottom: 12 }} />
        <Typography variant="caption" style={{ fontFamily: 'monospace', display: 'block' }}>
          <strong>Required field:</strong> repo_name (string)
        </Typography>
        <Typography variant="caption" style={{ fontFamily: 'monospace', display: 'block', marginTop: 4 }}>
          <strong>Optional fields:</strong> runner_id, workflow_run_number, jira_ticket_id,
          quality_score, score_passed, minimum_score, model_name, ai_provider,
          aws_cost, confluence_url, branch_name, pr_number, pr_creator, run_timestamp
        </Typography>
      </Paper>

      <Paper elevation={1} style={{ padding: 20 }}>
        <Typography variant="subtitle2" style={{ fontWeight: 700, marginBottom: 8 }}>
          Example GitHub Actions step
        </Typography>
        <Box
          component="pre"
          style={{
            background: '#F5F5F5',
            borderRadius: 6,
            padding: 16,
            fontSize: '0.75rem',
            overflowX: 'auto',
            margin: 0,
          }}
        >
{`- name: Report test quality to Backstage
  if: always()
  run: |
    curl -s -X POST \\
      "${endpoint}/record" \\
      -H "Content-Type: application/json" \\
      -d '{
        "repo_name": "$\{{ github.repository }}",
        "runner_id": "$\{{ github.run_id }}",
        "workflow_run_number": $\{{ github.run_number }},
        "quality_score": $\{{ steps.analyze.outputs.quality_score }},
        "score_passed": $\{{ steps.analyze.outputs.score_passed }},
        "minimum_score": $\{{ steps.analyze.outputs.minimum_score }},
        "model_name": "$\{{ steps.analyze.outputs.model_name }}",
        "ai_provider": "$\{{ steps.analyze.outputs.ai_provider }}",
        "aws_cost": $\{{ steps.analyze.outputs.aws_cost }},
        "branch_name": "$\{{ github.ref_name }}",
        "pr_number": $\{{ github.event.number || 0 }},
        "jira_ticket_id": "$\{{ env.JIRA_TICKET }}",
        "confluence_url": "$\{{ steps.analyze.outputs.confluence_url }}"
      }'`}
        </Box>
      </Paper>
    </Box>
  );
}

// ─── Main Page ────────────────────────────────────────────────────────────────

export const TestQualityPage = () => {
  const discoveryApi = useApi(discoveryApiRef);
  const fetchApi = useApi(fetchApiRef);
  const [baseUrl, setBaseUrl] = useState<string | null>(null);
  const [tab, setTab] = useState(0);
  const [searchParams, setSearchParams] = useSearchParams();

  // Persist selected repo in URL (?repo=owner/repo-name) so refresh keeps state
  const selectedRepo = searchParams.get('repo');
  const setSelectedRepo = (repo: string | null) => {
    if (repo) {
      setSearchParams({ repo });
    } else {
      setSearchParams({});
    }
  };

  useEffect(() => {
    discoveryApi.getBaseUrl('test-quality-tracker').then(setBaseUrl);
  }, [discoveryApi]);

  if (!baseUrl) {
    return (
      <Page themeId="tool">
        <Header title="Test Quality Tracker" subtitle="AI-powered test quality analysis" />
        <Content>
          <Box display="flex" justifyContent="center" py={8}>
            <CircularProgress />
          </Box>
        </Content>
      </Page>
    );
  }

  return (
    <Page themeId="tool">
      <Header
        title="Test Quality Tracker"
        subtitle="Monitor AI test quality analysis runs from GitHub Actions"
      />
      <Content>
        {selectedRepo ? (
          <RepoHistoryPanel
            repoName={selectedRepo}
            baseUrl={baseUrl}
            fetchApi={fetchApi}
            discoveryApi={discoveryApi}
            onBack={() => setSelectedRepo(null)}
          />
        ) : (
          <Box>
            <Tabs
              value={tab}
              onChange={(_, v) => setTab(v)}
              indicatorColor="primary"
              textColor="primary"
              style={{ marginBottom: 24 }}
            >
              <Tab label="Repositories" icon={<StorageIcon />} />
              <Tab label="Statistics" icon={<AssessmentIcon />} />
              <Tab label="API / Integration" icon={<TrendingUpIcon />} />
            </Tabs>

            {tab === 0 && (
              <ReposPanel
                baseUrl={baseUrl}
                fetchApi={fetchApi}
                onSelectRepo={repo => setSelectedRepo(repo)}
              />
            )}
            {tab === 1 && <StatsPanel baseUrl={baseUrl} fetchApi={fetchApi} />}
            {tab === 2 && <ApiDocsPanel baseUrl={baseUrl} />}
          </Box>
        )}
      </Content>
    </Page>
  );
};
